import { createAction, createReducer, on, props } from "@ngrx/store";
import { AppState } from "../redux/store";
import { Tab } from "../models/tab.model";
import { UiUser, UserRole } from "../models/ui-user.model";
import { ServiceMetaData } from "../models/service-meta.model";
import {
  DrProfile,
  Order,
  OrderFile,
  OrderLine,
  OrderSearchParams,
  PresetNote,
  RXForm,
  RXItem,
  RXNote,
  RXOpt,
  SystemParamSubject,
} from "../models/order.model";
import { Meta } from "@angular/platform-browser";
import { Clinic } from "../models/clinic.model";
import { DataElement, Doctor } from "../models/common";
import {
  ChatContact,
  ChatMsg,
  ChatTask,
  UserNotification,
} from "../models/chat.model";
import { SupplyOrder } from "../models/supply-order.model";
import {
  BalanceRow,
  Invoice,
  InvoiceReportsSearchParams,
  REPORTS_SCREEN_TYPE_ENUM,
  Receipt,
} from "../models/invoices.model";
import { DashboardData } from "../models/dashboard.model";
import { OperationReport } from "../models/operation_reports.model";
import { KpiConfig, LabKpiModel } from "../models/kpi.model";
import { AgentTask } from "../models/agent-tasks.model";
import { DistributionWaybillsResponse } from "../models/delivery.model";

export const initialState: AppState = new AppState();

export const UI__REQUEST_SET_LANG = createAction(
  "UI__REQUEST_SET_LANG",
  props<{ lang: string }>()
);

export const UI__SET_LANG = createAction(
  "UI__SET_LANG",
  props<{ lang: string; direction: string }>()
);

export const UI__ROUTE_NAVIGATION = createAction(
  "UI__ROUTE_NAVIGATION",
  props<{ data: Tab }>()
);

export const UI__SIDEBAR_ROUTE_NAVIGATION = createAction(
  "UI__SIDEBAR_ROUTE_NAVIGATION",
  props<{ data: Tab }>()
);

export const UI__SET_ACTIVE_TABS = createAction(
  "UI__SET_ACTIVE_TABS",
  props<{ data: Tab[] }>()
);

export const UI__TAB_NAVIGATION = createAction(
  "UI__TAB_NAVIGATION",
  props<{ data: Tab }>()
);

export const UI__ADD_TO_TAB_STORE = createAction(
  "UI__ADD_TO_TAB_STORE",
  props<{ tabStoreKey: string; tabs: Tab[] }>()
);

export const UI__REMOVE_FROM_TAB_STORE = createAction(
  "UI__REMOVE_FROM_TAB_STORE",
  props<{ tabStoreKey: string }>()
);

export const UI__TOGGLE_MOBILE_NAV = createAction(
  "UI__TOGGLE_MOBILE_NAV",
  props<{ mobileNavOpen: boolean }>()
);

export const ERROR__FROM_SERVER = createAction(
  "ERROR__FROM_SERVER",
  props<{ data: any }>()
);

export const USER_SWITCH_CLINIC = createAction(
  "USER_SWITCH_CLINIC",
  props<{ clinicId: string }>()
);

export const USER_SIGN_OUT = createAction("USER_SIGN_OUT");

export const GET_ORDERS = createAction(
  "GET_ORDERS",
  props<{ data: Order[]; medataData: ServiceMetaData; qcModeSw: boolean }>()
);

export const GET_DELIVERERS_LIST = createAction(
  "GET_DELIVERERS_LIST",
  props<{ data: DataElement[] }>()
);

export const RUN_ORDERS_SEARCH = createAction(
  "RUN_ORDERS_SEARCH",
  props<{ newSearch: boolean }>()
);

export const GET_ORDER_HEADER_INIT = createAction(
  "GET_ORDER_HEADER_INIT",
  props<{ orderSearchMetadata: ServiceMetaData }>()
);

export const GET_CLINIC_LIST = createAction(
  "GET_CLINIC_LIST",
  props<{ data: Clinic[] }>()
);

export const GET_CLINIC_CHAIN_LIST = createAction(
  "GET_CLINIC_CHAIN_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_SYSTEM_PARAMS = createAction(
  "GET_SYSTEM_PARAMS",
  props<{ data: SystemParamSubject[] }>()
);

export const GET_DOCTOR_LIST = createAction(
  "GET_DOCTOR_LIST",
  props<{ data: Doctor[] }>()
);

export const GET_URGENCY_LIST = createAction(
  "GET_URGENCY_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_STAGE_LIST = createAction(
  "GET_STAGE_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_CHAT_TASK_STATUS_LIST = createAction(
  "GET_CHAT_TASK_STATUS_LIST",
  props<{ data: DataElement[] }>()
);

export const CREATED_CHAT_TASK = createAction(
  "CREATED_CHAT_TASK",
  props<{ data: ChatTask }>()
);

export const GET_PLANT_LIST = createAction(
  "GET_PLANT_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_ROLE_LIST = createAction(
  "GET_ROLE_LIST",
  props<{ data: UserRole[] }>()
);

export const GET_SUPPLY_TYPES_LIST = createAction(
  "GET_SUPPLY_TYPES_LIST",
  props<{ data: DataElement[] }>()
);

export const SELECT_INBOX_ORDER = createAction(
  "SELECT_INBOX_ORDER",
  props<{ order: Order }>()
);

export const PATCH_INBOX_ORDER = createAction(
  "PATCH_INBOX_ORDER",
  props<{ orderId: number; changes: Partial<Order> }>()
);

export const RELOAD_CURRENT_ORDER = createAction(
  "RELOAD_CURRENT_ORDER",
  props<{ order: Order }>()
);

export const FOCUS_ORDER_CHAT_TAB = createAction(
  "FOCUS_ORDER_CHAT_TAB",
  props<{ focusOrderChatTab: boolean }>()
);

export const GET_SUPPLY_ORDERS = createAction(
  "GET_SUPPLY_ORDERS",
  props<{ data: SupplyOrder[]; medataData: ServiceMetaData }>()
);

export const APPROVE_SUPPLY_ORDER = createAction("APPROVE_SUPPLY_ORDER");

export const CREATE_SUPPLY_ORDER = createAction("CREATE_SUPPLY_ORDER");

export const SUPPLIES_SELECT_CLINIC = createAction(
  "SUPPLIES_SELECT_CLINIC",
  props<{ clinic: DataElement }>()
);

export const RUN_USERS_SEARCH = createAction(
  "RUN_USERS_SEARCH",
  props<{ newSearch: boolean }>()
);

export const RUN_CLINICS_SEARCH = createAction(
  "RUN_CLINICS_SEARCH",
  props<{ newSearch: boolean }>()
);

export const GET_USERS = createAction(
  "GET_USERS",
  props<{ data: UiUser[]; medataData: ServiceMetaData }>()
);

export const GET_TASK_USERS = createAction(
  "GET_TASK_USERS",
  props<{ data: UiUser[]; medataData: ServiceMetaData }>()
);

export const GET_KPI_CONFIG = createAction(
  "GET_KPI_CONFIG",
  props<{ data: KpiConfig[] }>()
);

export const SET_DISPLAYED_FILE = createAction(
  "SET_DISPLAYED_FILE",
  props<{ fileId: string }>()
);

export const CLEAR_DISPLAYED_FILE = createAction(
  "CLEAR_DISPLAYED_FILE"
);

export const GET_CLINIC_DETAILS = createAction(
  "GET_CLINIC_DETAILS",
  props<{ data: Clinic[]; medataData: ServiceMetaData }>()
);

export const EDIT_USER = createAction("EDIT_USER", props<{ user: UiUser }>());

export const UPDATE_USER = createAction(
  "UPDATE_USER",
  props<{ user: UiUser }>()
);

export const GET_USER_NOTIFICATIONS = createAction(
  "GET_USER_NOTIFICATIONS",
  props<{ data: UserNotification[] }>()
);

export const SELECT_INBOX_RESULTS_PER_PAGE = createAction(
  "SELECT_INBOX_RESULTS_PER_PAGE",
  props<{ inboxResultsPerPage: number }>()
);

export const SELECT_CHAT_CONTACT = createAction(
  "SELECT_CHAT_CONTACT",
  props<{ chatContact: ChatContact }>()
);

export const SEND_FILE_CHAT_MSG = createAction(
  "SEND_FILE_CHAT_MSG",
  props<{ orderFile: OrderFile; chatContact: ChatContact }>()
);

export const SELECT_REPORT_SCREEN_TYPE = createAction(
  "SELECT_REPORT_SCREEN_TYPE",
  props<{ screenType?: REPORTS_SCREEN_TYPE_ENUM }>()
);

export const RUN_REPORTS_SEARCH = createAction(
  "RUN_REPORTS_SEARCH",
  props<{ searchParams?: InvoiceReportsSearchParams }>()
);

export const CLEAR_REPORTS_SEARCH = createAction("CLEAR_REPORTS_SEARCH");

export const GET_CLIENT_INVOICES = createAction(
  "GET_CLIENT_INVOICES",
  props<{ data: Invoice[]; medataData: ServiceMetaData }>()
);

export const GET_CLIENT_RECEIPTS = createAction(
  "GET_CLIENT_RECEIPTS",
  props<{ data: Receipt[]; medataData: ServiceMetaData }>()
);

export const GET_CLIENT_BALANCE = createAction(
  "GET_CLIENT_BALANCE",
  props<{ data: BalanceRow[]; medataData: ServiceMetaData }>()
);

export const GET_TOTAL_DEBT = createAction(
  "GET_TOTAL_DEBT",
  props<{ data: number }>()
);

export const SELECT_SUPPLY_TYPE = createAction(
  "SELECT_SUPPLY_TYPE",
  props<{ type?: DataElement }>()
);

export const GET_DASHBOARD_DATA = createAction(
  "GET_DASHBOARD_DATA",
  props<{ data: DashboardData }>()
);

export const REQUEST_DASHBOARD_REFRESH = createAction(
  "REQUEST_DASHBOARD_REFRESH",
  // skipNotifications: set by the layout when it dispatches from its own
  // notifications poll, so the layout's handler doesn't re-poll (feedback loop)
  (skipNotifications: boolean = false) => ({ skipNotifications })
);

export const GET_CLINIC_USERS = createAction(
  "GET_CLINIC_USERS",
  props<{ clinic: Clinic }>()
);

export const SELECT_CHAT_TASK = createAction(
  "SELECT_CHAT_TASK",
  props<{ task: ChatTask }>()
);

export const GET_OPERATION_REPORTS = createAction(
  "GET_OPERATION_REPORTS",
  props<{ data: OperationReport[] }>()
);

export const GET_AGENTS_LIST = createAction(
  "GET_AGENTS_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_LABS_KPI = createAction(
  "GET_LABS_KPI",
  props<{ data: LabKpiModel }>()
);

export const GO_TO_CLINIC_KPI = createAction(
  "GO_TO_CLINIC_KPI",
  props<{ clinicId: number }>()
);

export const GET_AGENT_CLINIC_LIST = createAction(
  "GET_AGENT_CLINIC_LIST",
  props<{ data: Clinic[] }>()
);

export const GET_AGENT_TASKS = createAction(
  "GET_AGENT_TASKS",
  props<{ data: AgentTask[]; medataData: ServiceMetaData }>()
);

export const GET_DR_PROFILES = createAction(
  "GET_DR_PROFILES",
  props<{ data: DrProfile[]; medataData: ServiceMetaData }>()
);

export const RUN_DR_PROFILES_SEARCH = createAction(
  "RUN_DR_PROFILES_SEARCH",
  props<{ newSearch: boolean }>()
);

export const GET_RX_OPT = createAction(
  "GET_RX_OPT",
  props<{ data: RXOpt[] }>()
);

export const GET_TECHINICIANS_LIST = createAction(
  "GET_TECHINICIANS_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_ORDER_EMAIL_TEMPLATE_LIST = createAction(
  "GET_ORDER_EMAIL_TEMPLATE_LIST",
  props<{ data: DataElement[] }>()
);

export const GET_TECH_ISSUES_LIST = createAction(
  "GET_TECH_ISSUES_LIST",
  props<{ data: DataElement[] }>()
);

export const CREATE_TASK_FOR_CHAT_MSG = createAction(
  "CREATE_TASK_FOR_CHAT_MSG",
  props<{ msg: ChatMsg }>()
);

export const SELECT_RX_FORM = createAction(
  "SELECT_RX_FORM",
  props<{ form: RXForm }>()
);

export const GET_RX_PRESET_NOTES = createAction(
  "GET_RX_PRESET_NOTES",
  props<{ data: RXNote[] }>()
);

export const GET_RX_ITEMS = createAction(
  "GET_RX_ITEMS",
  props<{ data: RXItem[] }>()
);

export const GET_PRESET_NOTES = createAction(
  "GET_PRESET_NOTES",
  props<{ data: PresetNote[]; medataData: ServiceMetaData }>()
);

export const GET_GENERAL_TASKS_USERS = createAction(
  "GET_GENERAL_TASKS_USERS",
  props<{ data: UiUser[] }>()
);

export const RELOAD_INBOX = createAction("RELOAD_INBOX");
export const MARK_INBOX_NEEDS_RELOAD = createAction("MARK_INBOX_NEEDS_RELOAD");
export const UI__HIGHLIGHT_TABS = createAction("UI__HIGHLIGHT_TABS");

export const NAVIGATE_TO_NEXT_ORDER_PAGE = createAction(
  "NAVIGATE_TO_NEXT_ORDER_PAGE"
);

export const NAVIGATE_TO_PREV_ORDER_PAGE = createAction(
  "NAVIGATE_TO_PREV_ORDER_PAGE"
);

export const GET_DISTRIBUTION_WAYBILLS = createAction(
  "GET_DISTRIBUTION_WAYBILLS",
  props<{ data: DistributionWaybillsResponse; medataData: ServiceMetaData }>()
);